import React, { useEffect, useState } from "react";
import { BrandLogo, LOGO_SRC } from "@/components/shared";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trophy, Menu, X, Zap, ShieldCheck, Gift, TrendingUp, Users, Sparkles,
  Mail, Phone, MapPin, ChevronRight, Target, Wallet, RefreshCw,
} from "lucide-react";
import LangToggle from "@/components/LangToggle";
import LiveClock from "@/components/LiveClock";
import { ZaraWidget } from "@/components/ZaraWidget";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const HERO = "https://images.unsplash.com/photo-1599158150601-1417ebbaafdd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TROPHY = "https://images.pexels.com/photos/6532373/pexels-photo-6532373.jpeg?auto=compress&cs=tinysrgb&w=940";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`max-w-6xl mx-auto px-5 py-20 ${className}`}>{children}</section>
);

export default function Home() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events/public").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const nav = [
    ["about", "about"], ["how_it_works", "how"], ["events", "events"],
    ["contact", "contact"], ["support", "support"],
  ];

  const scrollTo = (id) => { setOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <div className="ec-bg min-h-screen">
      {/* NAV */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-head font-extrabold text-lg" data-testid="home-brand">
            <BrandLogo size="w-11 h-11" text={t("brand")} textClass="text-lg hidden sm:inline" testid="home-brand-logo" />
          </Link>
          <nav className="hidden lg:flex items-center gap-6 text-sm text-gray-300">
            {nav.map(([k, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="hover:text-orange-400 transition-colors" data-testid={`nav-${id}`}>{t(k)}</button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LangToggle compact />
            <Button onClick={() => navigate("/login")} variant="ghost" className="hidden sm:inline-flex text-gray-200 hover:text-orange-400" data-testid="nav-login-button">{t("login")}</Button>
            <Button onClick={() => navigate("/register")} className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold" data-testid="nav-register-button">{t("create_account")}</Button>
            <button className="lg:hidden text-white ml-1" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-3">
            {nav.map(([k, id]) => (<button key={id} onClick={() => scrollTo(id)} className="text-start text-gray-200 py-1">{t(k)}</button>))}
            <button onClick={() => navigate("/login")} className="text-start text-orange-400 py-1">{t("login")}</button>
          </div>
        )}
      </header>

      {/* HERO */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="stadium" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F5F7FB]/70 via-[#F5F7FB]/85 to-[#F5F7FB]" />
        </div>
        <Section className="relative !py-28">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <img src={LOGO_SRC} alt="EC DREAM logo" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-contain mb-6 shadow-[0_12px_40px_-12px_rgba(11,36,85,0.45)]" data-testid="hero-logo" />
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-400 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5">
                <Sparkles className="w-3.5 h-3.5" /> {t("tagline")}
              </span>
              <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mt-6 leading-tight">{t("hero_title")}</h1>
              <p className="text-gray-300 text-lg mt-5 max-w-2xl leading-relaxed">{t("hero_sub")}</p>
              <div className="flex flex-wrap items-center gap-4 mt-8">
                <Button onClick={() => navigate("/register")} className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold text-base px-8 py-6" data-testid="hero-get-started">{t("get_started")} <ChevronRight className="w-4 h-4 ms-1" /></Button>
                <Button onClick={() => scrollTo("events")} variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white px-8 py-6" data-testid="hero-explore-events">{t("explore_events")}</Button>
              </div>
              <div className="mt-8"><LiveClock /></div>
            </motion.div>
          </div>
        </Section>
      </div>

      {/* ABOUT / COMPANY */}
      <Section id="about">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-orange-400 font-semibold">{t("company_info")}</p>
            <h2 className="font-head text-3xl lg:text-4xl font-bold text-white mt-3">{t("about")}</h2>
            <p className="text-gray-300 mt-5 leading-relaxed">{t("about_text")}</p>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[[TrendingUp, "10%+", t("reward_percentage")], [Users, "Referral", t("referral")], [ShieldCheck, "Secure", t("kyc")]].map(([Icon, a, b], i) => (
                <div key={i} className="ec-card p-4 text-center">
                  <Icon className="w-6 h-6 text-orange-400 mx-auto" />
                  <p className="font-head font-bold text-white mt-2">{a}</p>
                  <p className="text-xs text-gray-500">{b}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={TROPHY} alt="trophy" className="rounded-2xl border border-white/10 animate-floaty" />
            <div className="absolute -bottom-4 -end-4 ec-card p-4 glow-orange hidden sm:block">
              <p className="text-xs text-gray-400">{t("current_balance")}</p>
              <p className="font-head text-2xl font-extrabold text-gradient">Rs 25,000</p>
            </div>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" className="!py-16">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-widest text-orange-400 font-semibold">{t("how_it_works")}</p>
          <h2 className="font-head text-3xl lg:text-4xl font-bold text-white mt-3">{t("how_it_works")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[[Target, "how_step1", "how_step1d"], [Zap, "how_step2", "how_step2d"], [Gift, "how_step3", "how_step3d"]].map(([Icon, tt, dd], i) => (
            <div key={i} className="ec-card p-8 relative overflow-hidden">
              <span className="absolute top-4 end-5 font-head text-6xl font-extrabold text-white/5">{i + 1}</span>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center"><Icon className="w-6 h-6 text-orange-400" /></div>
              <h3 className="font-head text-xl font-semibold text-white mt-5">{t(tt)}</h3>
              <p className="text-gray-400 mt-2 text-sm leading-relaxed">{t(dd)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* EVENTS */}
      <Section id="events" className="!py-16">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-widest text-orange-400 font-semibold">{t("events")}</p>
          <h2 className="font-head text-3xl lg:text-4xl font-bold text-white mt-3">{t("upcoming_events")}</h2>
        </div>
        {events.length === 0 ? (
          <p className="text-center text-gray-500">{t("no_events")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev) => (
              <div key={ev.id} className="ec-card p-6" data-testid={`public-event-${ev.id}`}>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{fmtDate(ev.date + "T" + (ev.time || "00:00"))}</span>
                  <span className="text-orange-400 font-semibold">{ev.reward_percentage}%</span>
                </div>
                <h3 className="font-head text-lg font-semibold text-white mt-3">{ev.name}</h3>
                <div className="flex items-center justify-between mt-4 text-sm">
                  <span className="text-gray-200 font-medium">{ev.team_a}</span>
                  <span className="text-orange-500 font-head font-bold">VS</span>
                  <span className="text-gray-200 font-medium">{ev.team_b}</span>
                </div>
                <Button onClick={() => navigate("/login")} className="w-full mt-5 bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("predict_now")}</Button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* CONTACT + SUPPORT */}
      <Section id="contact" className="!py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="ec-card p-8">
            <h2 className="font-head text-2xl font-bold text-white">{t("contact_us")}</h2>
            <div className="space-y-4 mt-6 text-gray-300 text-sm">
              <p className="flex items-center gap-3"><Mail className="w-5 h-5 text-orange-400" /> support@earncashdream.com</p>
              <p className="flex items-center gap-3"><Phone className="w-5 h-5 text-orange-400" /> +92 300 0000000</p>
              <p className="flex items-center gap-3"><MapPin className="w-5 h-5 text-orange-400" /> Karachi, Pakistan</p>
            </div>
          </div>
          <div id="support" className="ec-card p-8">
            <h2 className="font-head text-2xl font-bold text-white">{t("get_support")}</h2>
            <p className="text-gray-400 mt-3 text-sm leading-relaxed">Our team is available around the clock. Create an account to open a support ticket and track responses right from your member dashboard.</p>
            <Button onClick={() => navigate("/register")} className="mt-6 bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("create_account")}</Button>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 bg-pure">
        <div className="max-w-6xl mx-auto px-5 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <BrandLogo size="w-12 h-12" text={t("brand")} textClass="text-lg" testid="footer-brand-logo" />
            <p className="text-gray-500 text-sm mt-4 leading-relaxed">{t("tagline")}</p>
          </div>
          <div>
            <p className="font-semibold text-white mb-4">{t("home")}</p>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><button onClick={() => scrollTo("about")} className="hover:text-orange-400">{t("about")}</button></li>
              <li><button onClick={() => scrollTo("how")} className="hover:text-orange-400">{t("how_it_works")}</button></li>
              <li><button onClick={() => scrollTo("events")} className="hover:text-orange-400">{t("events")}</button></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-white mb-4">{t("support")}</p>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><button onClick={() => scrollTo("contact")} className="hover:text-orange-400">{t("contact")}</button></li>
              <li><button onClick={() => scrollTo("support")} className="hover:text-orange-400">{t("support")}</button></li>
              <li><Link to="/login" className="hover:text-orange-400">{t("login")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-white mb-4">{t("current_balance")}</p>
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-3"><Wallet className="w-4 h-4 text-orange-400" /> PKR Wallet</div>
            <div className="flex items-center gap-2 text-gray-400 text-sm"><RefreshCw className="w-4 h-4 text-orange-400" /> {t("daily_spin")}</div>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-gray-600">
          © {new Date().getFullYear()} {t("brand")}. All rights reserved.
        </div>
      </footer>

      {/* SECRET ADMIN ENTRY */}
      <div className="bg-[#EEF2FA] py-8 text-center">
        <button
          onClick={() => navigate("/admin/login")}
          data-testid="admin-secret-thankyou-link"
          className="font-head text-sm tracking-[0.3em] text-gray-600 hover:text-orange-500 transition-colors"
        >
          {t("thank_you")}
        </button>
      </div>
      <ZaraWidget />
    </div>
  );
}
